import { useState, useEffect } from 'react';
import { History, Monitor, MapPin, Calendar, LogOut, Chrome } from 'lucide-react';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import { getMyLoginHistory } from '../../../actions/session-manager';
import { toastMessage } from '../../../utils/toast-message';
import Loader from '../../../components/common/Loader/Loader';

interface LoginRecord {
  history_uuid: string;
  ip_address: string;
  device_info: string;
  user_agent: string;
  login_method: 'email' | 'facebook' | 'google' | 'whatsapp';
  login_status: 'success' | 'failed';
  failure_reason?: string;
  login_at: string;
  logout_at?: string;
  logout_type?: 'manual' | 'auto' | 'forced';
  session_duration?: number;
}

const LoginHistoryPage = () => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<LoginRecord[]>([]);
  const [filter, setFilter] = useState<'all' | 'success' | 'failed'>('all');

  useEffect(() => {
    fetchLoginHistory();
  }, []);

  const fetchLoginHistory = async () => {
    try {
      setLoading(true);
      const response = await getMyLoginHistory(50);
      console.log('Login history response:', response);

      if (response.status === 'success' && response.data?.history) {
        setHistory(response.data.history);
        console.log('History set:', response.data.history);
      }
    } catch (error) {
      console.error('Error fetching login history:', error);
      toastMessage('Failed to load login history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const parseDeviceInfo = (deviceInfoStr: string) => {
    try {
      return JSON.parse(deviceInfoStr);
    } catch {
      return { device: 'Unknown', browser: 'Unknown', platform: 'Unknown' };
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${seconds}s`;
  };

  const getLoginMethodBadge = (method: string) => {
    const colors = {
      email: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      facebook: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      google: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
    return colors[method as keyof typeof colors] || colors.email;
  };

  const getStatusBadge = (status: string) => {
    if (status === 'success') {
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    }
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  };

  const getLogoutTypeBadge = (type?: string) => {
    const colors = {
      manual: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      auto: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      forced: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    };
    return colors[type as keyof typeof colors] || colors.manual;
  };

  const filteredHistory = history.filter(record => {
    if (filter === 'all') return true;
    return record.login_status === filter;
  });

  if (loading) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="sticky top-0 z-10">
          <ModernSettingHeader
            title="Login History"
            description="View your account login activity and security events"
            icon={<History className="w-6 h-6" />} />
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-center h-64">
            <Loader size={42} text="Loading login history..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="sticky top-0 z-10">
        <ModernSettingHeader
          title="Login History"
          description="View your account login activity and security events"
          icon={<History className="w-6 h-6" />}
        />
      </div>
      <div className="flex-1 overflow-y-auto p-6">
      {/* Filter Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-brandColor text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            All ({history.length})
          </button>
          <button
            onClick={() => setFilter('success')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'success'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Successful ({history.filter(h => h.login_status === 'success').length})
          </button>
          <button
            onClick={() => setFilter('failed')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'failed'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Failed ({history.filter(h => h.login_status === 'failed').length})
          </button>
        </div>
      </div>

      {/* Login History Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {filteredHistory.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No login history found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Device & Browser
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    IP Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Session Info
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredHistory.map((record) => {
                  const deviceInfo = parseDeviceInfo(record.device_info);
                  return (
                    <tr key={record.history_uuid} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {new Date(record.login_at).toLocaleDateString()}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400">
                              {new Date(record.login_at).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLoginMethodBadge(record.login_method)}`}>
                          {record.login_method}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-start text-sm">
                          <Monitor className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {deviceInfo.device} - {deviceInfo.browser}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400 text-xs">
                              {deviceInfo.platform}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900 dark:text-white">
                          <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                          {record.ip_address || 'Unknown'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(record.login_status)}`}>
                          {record.login_status}
                        </span>
                        {record.failure_reason && (
                          <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                            {record.failure_reason}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {record.logout_at ? (
                          <div className="text-sm">
                            <div className="flex items-center text-gray-900 dark:text-white">
                              <LogOut className="w-4 h-4 mr-2 text-gray-400" />
                              <span>Duration: {formatDuration(record.session_duration)}</span>
                            </div>
                            {record.logout_type && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${getLogoutTypeBadge(record.logout_type)}`}>
                                {record.logout_type === 'manual' && 'Manual Logout'}
                                {record.logout_type === 'auto' && 'Auto Expired'}
                                {record.logout_type === 'forced' && 'Forced Logout'}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Security Tips */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mt-6 mb-6">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3 flex items-center">
          <Chrome className="w-5 h-5 mr-2" />
          Security Tips
        </h3>
        <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
          <li>• Review your login history regularly for any suspicious activity</li>
          <li>• If you see unfamiliar IP addresses or devices, change your password immediately</li>
          <li>• You can only be logged in from one device at a time for security</li>
          <li>• "Forced Logout" means you were logged out due to login from another device</li>
        </ul>
      </div>
      </div>
    </div>
  );
};

export default LoginHistoryPage;
